import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';

export const SearchSelect = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select...',
  className = '',
  disabled = false,
  size = 'md',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const buttonRef = useRef(null);

  const selectedOption = options.find(opt => opt.value === value);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 300;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow < dropdownHeight && rect.top > spaceBelow
        ? rect.top - dropdownHeight - 8
        : rect.bottom + 8;
      setMenuPosition({ top, left: rect.left, width: rect.width });
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const paddingBySize = { sm: '6px 8px', md: '8px 12px', lg: '10px 16px' };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }} className={className}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 6,
          padding: paddingBySize[size] || paddingBySize.md,
          background: disabled ? '#F1F5F9' : '#FFFFFF',
          border: `1.5px solid ${isOpen ? '#2563EB' : '#E2E8F0'}`,
          borderRadius: 10,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'border-color .15s',
          fontFamily: 'inherit',
        }}
        onMouseEnter={e => { if (!disabled && !isOpen) e.currentTarget.style.borderColor = '#CBD5E1'; }}
        onMouseLeave={e => { if (!isOpen) e.currentTarget.style.borderColor = '#E2E8F0'; }}
      >
        <span style={{
          fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
          color: selectedOption ? '#1E293B' : '#94A3B8',
        }}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          size={15}
          color="#94A3B8"
          style={{ flexShrink: 0, transition: 'transform .15s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            width: menuPosition.width,
            zIndex: 9999,
            maxHeight: 300,
            background: '#FFFFFF',
            border: '1.5px solid #E2E8F0',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,.12)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Search input */}
          <div style={{ padding: '8px 8px 6px', borderBottom: '1px solid #F1F5F9', flexShrink: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} color="#94A3B8" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search..."
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '7px 10px 7px 30px',
                  border: '1.5px solid #E2E8F0', borderRadius: 8,
                  fontSize: 12, color: '#1E293B', outline: 'none',
                  fontFamily: 'inherit',
                }}
                onFocus={e => { e.target.style.borderColor = '#2563EB'; }}
                onBlur={e => { e.target.style.borderColor = '#E2E8F0'; }}
              />
            </div>
          </div>

          {/* Options */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option)}
                  style={{
                    width: '100%', display: 'block', textAlign: 'left',
                    padding: '8px 12px', border: 'none', cursor: 'pointer',
                    fontSize: 13, fontFamily: 'inherit',
                    background: option.value === value ? '#2563EB' : 'transparent',
                    color: option.value === value ? '#FFFFFF' : '#1E293B',
                  }}
                  onMouseEnter={e => { if (option.value !== value) e.currentTarget.style.background = '#F8FAFC'; }}
                  onMouseLeave={e => { if (option.value !== value) e.currentTarget.style.background = 'transparent'; }}
                >
                  {option.label}
                </button>
              ))
            ) : (
              <div style={{ padding: '16px 12px', fontSize: 13, color: '#94A3B8', textAlign: 'center' }}>
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchSelect;
